<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="4"
        :min-h="minHeight"
        :resized="handleResized"
        :key="cardGridKey"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <div class="d-flex flex-md-row flex-column todo-application h-100 min-height-100">
                <div class="todo-sidebar d-none d-md-flex flex-column mb-md-0 mb-3">
                    <b-button variant="primary" class="ml-auto mr-auto w-75 add-task-button" @click="showAddTodoModal"
                        >Add Task</b-button
                    >
                    <b-list-group class="list-group-filters mt-4">
                        <b-list-group-item
                            v-for="category in categories"
                            :key="category.id"
                            href="#"
                            :active="category.active"
                            class="d-flex align-items-center"
                            @click="category.click"
                        >
                            <font-awesome-icon :icon="getTodoCategoryIcon(category.value)" />
                            <span class="align-text-bottom line-height-1 d-block ml-3">{{ category.label }}</span>
                        </b-list-group-item>
                    </b-list-group>
                </div>
                <div class="todo-app-list-wrapper h-100">
                    <div class="d-flex d-md-none justify-content-between">
                        <div class="app-fixed-search app-fixed-search_mobile d-flex w-75 align-items-center">
                            <div class="d-flex align-content-center justify-content-between w-100">
                                <b-input-group class="input-group-merge">
                                    <b-input-group-prepend is-text>
                                        <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                    </b-input-group-prepend>
                                    <b-form-input v-model="search" @input="searchTodo" />
                                </b-input-group>
                            </div>

                            <div class="dropdown">
                                <b-dropdown variant="link" no-caret toggle-class="p-0 mr-1" right>
                                    <template #button-content>
                                        <font-awesome-icon
                                            class="text-secondary"
                                            :icon="['fas', 'ellipsis-vertical']"
                                        />
                                    </template>
                                    <b-dropdown-item
                                        :key="sort.title"
                                        v-for="sort in sortOptions"
                                        @click="setSort(sort.sortParams)"
                                    >
                                        {{ sort.title }}
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                        </div>
                        <responsive-button variant="primary" :icon="['fas', 'plus']" @click="showAddTodoModal"
                            >Add Task</responsive-button
                        >
                        <responsive-dropdown :icon="currentCategoryIcon" :items="categories" variant="primary">{{
                            currentFilter.label
                        }}</responsive-dropdown>
                    </div>

                    <div class="app-fixed-search d-none d-md-flex align-items-center">
                        <div class="d-flex align-content-center justify-content-between w-100">
                            <b-input-group class="input-group-merge">
                                <b-input-group-prepend is-text>
                                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                </b-input-group-prepend>
                                <b-form-input placeholder="Search task" v-model="search" @input="searchTodo" />
                            </b-input-group>
                        </div>

                        <div class="dropdown">
                            <b-dropdown variant="link" no-caret toggle-class="p-0 mr-1" right>
                                <template #button-content>
                                    <font-awesome-icon class="text-secondary" :icon="['fas', 'ellipsis-vertical']" />
                                </template>
                                <b-dropdown-item
                                    :key="sort.title"
                                    v-for="sort in sortOptions"
                                    @click="setSort(sort.sortParams)"
                                >
                                    {{ sort.title }}
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>

                    <div class="todo-app-list">
                        <div class="todo-task-list-wrapper">
                            <div class="todo-task-list media-list">
                                <draggable :sort="true" v-model="tasks" handle=".handle" @end="(e) => onDragEnd(e)">
                                    <li
                                        v-for="task in tasks"
                                        :key="task.id"
                                        :id="task.id"
                                        class="todo-item"
                                        :class="{
                                            completed: task.is_completed,
                                        }"
                                    >
                                        <div class="todo-title-wrapper">
                                            <div class="todo-title-area">
                                                <font-awesome-icon
                                                    v-if="isAllFilter"
                                                    :icon="['fas', 'bars']"
                                                    class="dnd-icon mr-4 handle"
                                                />
                                                <div class="title-wrapper">
                                                    <b-form-checkbox
                                                        v-if="isAllFilter"
                                                        :checked="task.is_completed"
                                                        @change="(val) => onCheckBoxChange(val, task)"
                                                    />
                                                    <span class="todo-title">{{ task.title }}</span>
                                                </div>
                                            </div>
                                            <div class="todo-item-action">
                                                <div class="tags-wrapper">
                                                    <div
                                                        :key="tag.id"
                                                        :style="{
                                                            ...getTagsStyles(tag.color),
                                                        }"
                                                        v-for="tag in task.tags"
                                                        class="todo-tag mr-2"
                                                    >
                                                        <span>
                                                            {{ tag.title }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <small class="text-nowrap text-muted mr-2">{{ task.due_date }}</small>
                                                <b-avatar
                                                    size="32"
                                                    variant="primary"
                                                    :text="avatarText(task.assignee && task.assignee.name)"
                                                />
                                                <span
                                                    v-if="!task.is_deleted"
                                                    class="ml-3 action-icon"
                                                    @click="showEditTodoModal(task)"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'pen-to-square']" />
                                                </span>
                                                <span
                                                    @click="showDeleteTodoModal(task)"
                                                    v-if="!task.is_deleted"
                                                    class="ml-3 action-icon mr-2"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'trash']" />
                                                </span>
                                                <span
                                                    @click="showRestoreTodoModal(task)"
                                                    v-if="task.is_deleted"
                                                    class="ml-3 action-icon"
                                                >
                                                    <font-awesome-icon :icon="['fas', 'arrow-rotate-right']" />
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                </draggable>
                            </div>
                        </div>
                        <div class="d-flex justify-content-center align-items-center h-100" v-if="!tasks.length">
                            <h5>No Items Found</h5>
                        </div>
                    </div>
                    <b-pagination
                        v-if="tasks.length"
                        class="pr-4 pt-2 pb-2 mb-0 todo-list-pagination"
                        v-model="currentPage"
                        @change="setOffset"
                        :total-rows="total_records"
                        :per-page="limit"
                        hide-goto-end-buttons
                        limit="3"
                        align="right"
                    />
                </div>
            </div>
            <create-edit-todo-modal
                :initialValues="initialValues"
                :modalId="modalId"
                :modalTitle="modalTitle"
                @submit="createUpdateTodo"
            />
            <modal :id="deleteTodoModalId" title="Delete todo" @ok="deleteTodo">
                <p>Are you sure you want to delete this todo?</p>
            </modal>
            <modal :id="restoreModalId" title="Restore todo" @ok="restoreTodo">
                <p>Are you sure you want to restore this todo?</p>
            </modal>
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
    import API from '../../../../api';
    import CardGridItem from '../common/card-grid-item.vue';
    import CreateEditTodoModal from './create-edit-todo-modal.vue';
    import {
        DASHBOARD_TODO_WIDGET_COMPLETED_LIST_ENDPOINT,
        DASHBOARD_TODO_WIDGET_COMPLETE_TODO_ENDPOINT,
        DASHBOARD_TODO_WIDGET_CREATE_TODO_ENDPOINT,
        DASHBOARD_TODO_WIDGET_DELETED_LIST_ENDPOINT,
        DASHBOARD_TODO_WIDGET_NOT_DELETED_LIST_ENDPOINT,
        DASHBOARD_TODO_WIDGET_UPDATE_DELETE_ENDPOINT,
        DASHBOARD_TODO_WIDGET_UNCOMPLETE_ENDPOINT,
        DASHBOARD_TODO_WIDGET_RESTORE_ENDPOINT,
    } from '../../../../endpoints';
    import debounce from 'lodash/debounce';
    import { VueDraggable } from 'vue-draggable-plus';
    import FormDatepicker from '../../../generic/form-datepicker/form-datepicker.vue';
    import formInput from '../../../generic/form-input/form-input.vue';
    import formSelect from '../../../generic/form-select/form-select.vue';
    import FormTextarea from '../../../generic/form-textarea/form-textarea.vue';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import { getTagsStyles, getWidgetTodoListRowsNumber } from '../helpers';
    import { isLargeScreen } from '../../../../utils/screens';
    import Modal from '../../../generic/modal/modal.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../../generic/responsive-dropdown/responsive-dropdown.vue';
    import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
    import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

    export default {
        name: 'TodoListCardGridItem',
        mixins: [cardGridItemBaseMixin],
        components: {
            draggable: VueDraggable,
            CardGridItem,
            CreateEditTodoModal,
            FormDatepicker,
            formInput,
            formSelect,
            FormTextarea,
            Modal,
            ResponsiveButton,
            ResponsiveDropdown,
            TabWidgetSettingsModalForm,
        },
        data() {
            return {
                busy: null,
                offset: 0,
                limit: getWidgetTodoListRowsNumber({
                    tabWidget: this.tabWidget,
                }),
                sort: 'order',
                desc: false,
                search: '',
                currentPage: 1,
                currentFilter: { label: 'All', value: 'all' },
                tasks: [],
                total_records: [],
                modalId: null,
                initialValues: null,
                deleteTodoModalId: null,
                restoreModalId: null,
                search: null,
            };
        },
        mounted() {
            this.fetchTodos();
        },
        methods: {
            avatarText(value) {
                if (!value) return '';
                const nameArray = value.split(' ');
                return nameArray.map((word) => word.charAt(0).toUpperCase()).join('');
            },
            getTagsStyles: getTagsStyles,
            async deleteTodo() {
                const id = this.deleteTodoModalId.split('delete-todo-modal')[1];
                try {
                    await API.delete(DASHBOARD_TODO_WIDGET_UPDATE_DELETE_ENDPOINT(id));
                    if (this.tasks.length === 1 && this.currentPage !== 1) {
                        this.currentPage = this.currentPage - 1;
                        await this.fetchTodos();
                    } else {
                        await this.fetchTodos();
                    }
                } catch {
                    this.errorToast({ text: 'Could not delete task' });
                }
            },
            checkMove(evt) {
                this.draggedElem = evt.draggedContext;
                this.relatedElem = evt.relatedContext;
            },
            async createUpdateTodo(value) {
                try {
                    this.modalId === 'add-todo-modal'
                        ? await API.post(DASHBOARD_TODO_WIDGET_CREATE_TODO_ENDPOINT(), value)
                        : await API.patch(DASHBOARD_TODO_WIDGET_UPDATE_DELETE_ENDPOINT(value.id), value);
                    this.$bvModal.hide(this.modalId);
                    await this.fetchTodos();
                } catch (e) {
                    this.errorToast({ text: 'Could not save data' });
                }
            },
            async onDragEnd(evt) {
                const target = evt.data;
                const newOrder = evt.newIndex + 1;
                try {
                    await API.patch(DASHBOARD_TODO_WIDGET_UPDATE_DELETE_ENDPOINT(target.id), {
                        ...target,
                        order: newOrder,
                    });
                } catch (e) {
                    this.errorToast({ text: 'Could not move todo' });
                }
            },
            getTodoCategoryIcon(filter) {
                switch (filter) {
                    case 'completed':
                        return ['fas', 'check'];
                    case 'deleted':
                        return ['fas', 'trash'];
                    case 'all':
                        return ['fas', 'list'];
                    default:
                        return ['fas', 'list'];
                }
            },
            async onCheckBoxChange(value, todo) {
                try {
                    const url = value
                        ? DASHBOARD_TODO_WIDGET_COMPLETE_TODO_ENDPOINT(todo.id)
                        : DASHBOARD_TODO_WIDGET_UNCOMPLETE_ENDPOINT(todo.id);
                    await API.post(url);
                    todo.is_completed = value;
                } catch (e) {
                    todo.is_completed = !value;
                    this.errorToast({ text: 'Could not update status' });
                }
            },
            searchTodo: debounce(function () {
                this.currentPage = 1;
                this.fetchTodos();
            }, 500),
            async fetchTodos() {
                try {
                    const url = this.isDeletedFilter
                        ? DASHBOARD_TODO_WIDGET_DELETED_LIST_ENDPOINT()
                        : this.isCompletedFilter
                        ? DASHBOARD_TODO_WIDGET_COMPLETED_LIST_ENDPOINT()
                        : DASHBOARD_TODO_WIDGET_NOT_DELETED_LIST_ENDPOINT();
                    const pageNumber = this.currentPage - 1;
                    this.offset = this.limit * pageNumber;
                    const params = {
                        offset: this.offset,
                        limit: this.limit,
                        search: this.search,
                        sort: this.sort,
                        desc: this.desc,
                    };
                    if (this.isDeletedFilter) {
                        const prevDate = new Date().setDate(new Date().getDate() - 30);
                        params.deleted_start_date = format(parseISO(new Date(prevDate).toISOString()), 'yyyy-MM-dd');
                    }
                    const response = await API.get(url, {
                        params,
                    });
                    this.tasks = response.data.data;
                    this.total_records = response.data.total_records;
                } catch (e) {
                    this.errorToast({ text: 'Could not load data' });
                }
            },
            isLargeScreen,
            handleResized() {
                this.limit = getWidgetTodoListRowsNumber({
                    tabWidget: this.tabWidget,
                });
                this.currentPage = 1;
                this.fetchTodos();
                this.cardGridKey += 1;
            },
            async restoreTodo() {
                const id = this.restoreModalId.split('restore-todo-modal')[1];
                try {
                    await API.post(DASHBOARD_TODO_WIDGET_RESTORE_ENDPOINT(id));
                    await this.fetchTodos();
                } catch {
                    this.errorToast({ text: 'Could not restore task' });
                }
            },
            setFilter(filter) {
                this.currentFilter = filter;
                this.currentPage = 1;
                this.fetchTodos();
            },
            setOffset(value) {
                this.currentPage = value;
                this.fetchTodos();
            },
            setSort(value) {
                this.sort = value.sort;
                this.desc = value.desc;
                this.fetchTodos();
            },
            showAddTodoModal() {
                this.modalId = 'add-todo-modal';
                this.initialValues = null;
                this.$nextTick(() => {
                    this.$bvModal.show(this.modalId);
                });
            },
            showDeleteTodoModal(todo) {
                this.deleteTodoModalId = 'delete-todo-modal' + todo.id;
                this.$nextTick(() => {
                    this.$bvModal.show(this.deleteTodoModalId);
                });
            },
            showEditTodoModal(todo) {
                this.initialValues = { ...todo };
                this.modalId = `edit-todo-modal` + todo.id;
                this.$nextTick(() => {
                    this.$bvModal.show(this.modalId);
                });
            },
            showRestoreTodoModal(todo) {
                this.restoreModalId = `restore-todo-modal` + todo.id;
                this.$nextTick(() => {
                    this.$bvModal.show(this.restoreModalId);
                });
            },
        },
        computed: {
            modalTitle() {
                return this.modalId === 'add-todo-modal' ? 'Add ToDo' : 'Edit ToDo';
            },
            currentCategoryIcon() {
                switch (this.currentFilter.value) {
                    case 'completed':
                        return ['fas', 'check'];
                    case 'deleted':
                        return ['fas', 'trash'];
                    case 'all':
                        return ['fas', 'list'];
                    default:
                        return ['fas', 'list'];
                }
            },
            categories() {
                const filterAll = {
                    id: '1',
                    title: 'All',
                    value: 'all',
                };
                const filterByCompleted = {
                    id: '2',
                    title: 'Completed',
                    value: 'completed',
                };
                const filterByDeleted = {
                    id: '3',
                    title: 'Deleted',
                    value: 'deleted',
                };
                return [
                    {
                        icon: this.getTodoCategoryIcon(filterAll.value),
                        id: filterAll.id,
                        label: filterAll.title,
                        value: filterAll.value,
                        active: this.currentFilter.value === filterAll.value,
                        click: () => {
                            this.setFilter(filterAll);
                        },
                    },
                    {
                        icon: this.getTodoCategoryIcon(filterByCompleted.value),
                        id: filterByCompleted.id,
                        label: filterByCompleted.title,
                        value: filterByCompleted.value,
                        active: this.currentFilter.value === filterByCompleted.value,
                        click: () => {
                            this.setFilter(filterByCompleted);
                        },
                    },
                    {
                        icon: this.getTodoCategoryIcon(filterByDeleted.value),
                        id: filterByDeleted.id,
                        label: filterByDeleted.title,
                        value: filterByDeleted.value,
                        active: this.currentFilter.value === filterByDeleted.value,
                        click: () => {
                            this.setFilter(filterByDeleted);
                        },
                    },
                ];
            },
            isAllFilter() {
                return this.currentFilter.value === 'all';
            },
            isDeletedFilter() {
                return this.currentFilter.value === 'deleted';
            },
            isCompletedFilter() {
                return this.currentFilter.value === 'completed';
            },
            sortOptions() {
                return [
                    {
                        title: 'Reset Sort',
                        sortParams: {
                            sort: 'id',
                            desc: false,
                        },
                    },
                    {
                        title: 'Sort A-Z',
                        sortParams: {
                            sort: 'title',
                            desc: false,
                        },
                    },
                    {
                        title: 'Sort Z-A',
                        sortParams: {
                            sort: 'title',
                            desc: true,
                        },
                    },
                    {
                        title: 'Due Date',
                        sortParams: {
                            sort: 'due_date',
                            desc: false,
                        },
                    },
                ];
            },
        },
    };
</script>

<style lang="scss">
    @import '../../../../scss/bootstrap-extended/bootstrap-extended';
    @import '../../../../mixins.scss';

    $border-color: #ebe9f1;

    .todo-application {
        width: 100%;
        .todo-sidebar {
            flex: 2;

            .list-group-filters .list-group-item {
                padding: 0.58rem 1.5rem;
                border: 0;
                font-weight: $font-weight-bold;
                letter-spacing: 0.4px;
                border-left: 2px solid transparent;
                border-radius: 0;
                transition: all 0.2s;
                + .list-group-item.active {
                    margin-top: 0;
                }
                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 3px 10px 0 $border-color;
                    background: transparent;
                    color: $primary;
                }
                &:focus,
                &.active {
                    background: transparent;
                    color: $primary;
                    border-left: 2px solid $primary;
                }
            }
        }
        .todo-app-list-wrapper {
            display: flex;
            flex-direction: column;
            flex: 10;
            border-left: 1px solid $border-color;
            overflow-x: auto;
            .app-fixed-search {
                padding: 10px 18px;
                border-bottom: 1px solid $border-color;
                border-top: 1px solid $border-color;
                background-color: $white;
                &_mobile {
                    border-right: 1px solid $border-color;
                    padding: 0;
                    padding-right: 0.3rem;
                    margin-right: 0.3rem;
                }
                .input-group:focus-within {
                    box-shadow: none;
                }
                input,
                .input-group-text {
                    border: 0;
                    background-color: transparent;
                    box-shadow: none;
                }
            }
        }
        .todo-app-list {
            height: inherit;
            flex: 10;
            overflow-y: auto;
            scrollbar-width: thin;
            @include scroll-bar;
        }
        .dnd-icon {
            cursor: move;
            margin-top: 3px;
        }
        .todo-title {
            margin-left: 5px;
            margin-right: 5px;
        }
        .completed {
            .todo-title {
                color: $text-muted;
                text-decoration: line-through;
            }
        }
        .todo-task-list-wrapper {
            position: relative;
            background-color: $white;
            overflow-y: auto;
            border-radius: 0;
            .todo-task-list {
                padding: 0;
                margin: 0;
                list-style: none;
                .todo-item {
                    overflow-y: auto;
                    transition: all 0.2s, border-color 0s;
                    position: relative;
                    padding: 10px 20px;
                    .tags-wrapper {
                        display: flex;
                        margin-right: 1rem;
                        .todo-tag {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            padding: 0 7px;
                            border-radius: 40px;
                            font-weight: bold;
                        }
                    }

                    .custom-checkbox {
                        .custom-control-label::before,
                        .custom-control-label::after {
                            top: 0.07rem;
                        }

                        .custom-control-label::after {
                            background-size: 57%;
                        }
                        &.custom-control {
                            padding-left: 1.8rem;

                            .input-group & {
                                padding-left: 1.3rem;
                            }
                        }

                        .custom-control-input {
                            width: 1.285rem;
                            height: 1.285rem;
                        }

                        .custom-control-label {
                            font-size: $font-size-base;
                            position: static;
                            &::before,
                            &::after {
                                width: 18px !important;
                                height: 18px !important;
                                left: 0;
                            }
                        }
                        .custom-control-input:checked ~ .custom-control-label::before {
                            box-shadow: 0 2px 4px 0 rgba($custom-control-indicator-checked-bg, 0.4);
                        }

                        .custom-control-input:disabled ~ .custom-control-label::before {
                            border: none;
                            box-shadow: none !important;
                        }

                        .custom-control-input:focus ~ .custom-control-label::before {
                            border-color: none;
                            box-shadow: 0 2px 4px 0 rgba($custom-control-indicator-checked-bg, 0.4);
                        }
                    }

                    .custom-control-input:not(:disabled) {
                        &:active ~ .custom-control-label {
                            &::before {
                                background-color: $primary;
                                border-color: $primary;
                            }
                        }
                    }

                    &:not(:first-child) {
                        border-top: 1px solid $border-color;
                    }
                    &:last-child {
                        border-bottom: 1px solid $border-color;
                    }
                    &:hover {
                        box-shadow: 0 3px 10px 0 $border-color;
                        transition: all 0.2s;
                    }
                    .todo-title-wrapper {
                        display: flex;
                        justify-content: space-between;
                    }
                    .todo-title-area,
                    .title-wrapper {
                        display: flex;
                        align-items: center;
                    }
                    .todo-item-action {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        .action-icon {
                            cursor: pointer;
                            color: $secondary;
                            &:hover {
                                opacity: 0.8;
                            }
                        }
                    }
                }
            }
            .no-results {
                display: none;
                padding: 1.5rem;
                text-align: center;
                &.show {
                    display: block;
                }
            }
        }
    }
    @include media-breakpoint-down(sm) {
        .todo-application {
            .todo-list-pagination {
                border-bottom: 1px solid $border-color;
                border-right: 1px solid $border-color;
            }
            .todo-title {
                white-space: nowrap;
            }
        }
    }
</style>
